import { useState, useRef } from "react";
import "@mediapipe/face_detection";
import "@mediapipe/face_mesh";
import "@tensorflow/tfjs-backend-webgl";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import { HomeModule } from "../HomeModule/HomeModule";
import { ScanModule } from "../ScanModule/ScanModule";
import { TokenUrl, AmplitudeUrl } from "../../constants";
import "./styles.css";
import { Results } from "../Results/Results";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { GeoBlockPage } from "../GeoBlockPage/GeoBlockPage";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../context/ConfigContext";
import useAiResults from "../../hooks/useAiResults";
import { useUID } from "../../context/UIDContext";
import { useScanImage } from "../../context/ImageContext";
import { UserIntakeModule } from "../UserIntakeModule/UserIntakeModule";
import useProductResults from "../../hooks/useProductResults";
import { ProductsModule } from "../ProductsModule/ProductsModule";

export const Pipeline = () => {
  const { i18n } = useTranslation();
  const { aiResults, resetAiResults, fetchAiResults } = useAiResults();
  const { resetProductResults, fetchProductResultsV1, fetchProductResultsV2 } =
    useProductResults();
  const [analysisPhase, setAnalysisPhase] = useState("home");
  const [token, updateToken] = useState("");
  const faceDetectorRef = useRef(null);
  const { resetScanImage } = useScanImage();
  const { uid } = useUID();
  const { configData } = useConfig();

  const reset = () => {
    resetAiResults();
    resetScanImage();
    resetProductResults();
  };

  const fetchUserToken = () => {
    fetch(TokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ partnerName: configData.partnerName }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error("Failed to fetch!");
      })
      .then((responseJson) => {
        updateToken(responseJson.token);
        setAnalysisPhase("scanModule");

        // save amplitude token in firebase
        fetch(AmplitudeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: responseJson.token,
          },
          body: JSON.stringify({ uuid: uid }),
        }).catch((error) => {
          console.error("Error:", error);
        });
      })
      .catch((error) => {
        setAnalysisPhase("error");
      });
  };

  const getProducts = async () => {
    if (configData?.ProductModuleFlags?.version === "2.0.0") {
      return fetchProductResultsV2(token, i18n.language);
    } else {
      return fetchProductResultsV1(token, i18n.language);
    }
  };

  const loadModels = async () => {
    if (faceDetectorRef.current) return true;
    try {
      // Load Face Detector
      const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
      const detectorConfig = {
        runtime: "mediapipe",
        solutionPath: "libs/mediapipe",
      };
      faceDetectorRef.current = await faceLandmarksDetection.createDetector(
        model,
        detectorConfig,
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const runFaceModel = async (video) => {
    if (faceDetectorRef.current === null) return [];

    const faces = await faceDetectorRef.current.estimateFaces(video, {
      flipHorizontal: true,
    });
    return faces;
  };

  const renderAnalysisPhase = () => {
    switch (analysisPhase) {
      case "scanModule":
        return (
          <ScanModule
            apiToken={token}
            faceModel={runFaceModel}
            runNextModule={() => setAnalysisPhase("userIntakeModule")}
            onError={() => setAnalysisPhase("error")}
            onExitClick={() => setAnalysisPhase("home")}
          />
        );
      case "userIntakeModule":
        return (
          <UserIntakeModule
            apiToken={token}
            fetchAIResults={() => fetchAiResults(token)}
            runNextModule={() => setAnalysisPhase("results")}
            onError={() => setAnalysisPhase("error")}
          />
        );
      case "results":
        return (
          <Results
            results={aiResults}
            onProductClick={() => setAnalysisPhase("products")}
            onExitClick={() => setAnalysisPhase("home")}
          />
        );
      case "products":
        return (
          <ProductsModule
            getProducts={getProducts}
            onBackClick={() => setAnalysisPhase("results")}
          />
        );
      case "error":
        return <ErrorPage onResetClick={() => setAnalysisPhase("home")} />;
      case "geoBlock":
        return <GeoBlockPage onResetClick={() => setAnalysisPhase("home")} />;
      default:
        return (
          <HomeModule
            loadModels={loadModels}
            onGeoBlock={() => setAnalysisPhase("geoBlock")}
            runNextModule={() => {
              reset();
              fetchUserToken();
            }}
          />
        );
    }
  };

  return <div className="pipeline">{renderAnalysisPhase()}</div>;
};
